import { enumMembers, type ApiSchema } from "../apiSchema";
import { jsonBody, requestJson, withQuery } from "../http/httpClient";
import { readArray, readBoolean, readInteger, readNullableString, readObject, readOneOf, readOptionalString, readString, type JsonObject } from "../http/jsonDecoding";
import { parseCollectionVersionRef } from "../../shared/references/contentReferences";
import { readCollectionVersionSummary, type CollectionVersionSummary } from "./contentDecoders";

/**
 * The project's model versions and the Workflow library at each (module B,
 * `docs/components/workflows.md`). A Workflow is picked as one exact `VersionRef`, so a
 * Thread started from it always runs the same steps (FR-WF-2, FR-WF-6).
 */

/** A model version; its `reference` is a Thread's `content_version`. */
export type ContentVersion = CollectionVersionSummary;

export interface WorkflowListing {
  /** The model version listed. */
  readonly version: string;
  readonly workflows: readonly WorkflowSummary[];
}

export interface WorkflowSummary {
  /** `VersionRef` `<item id>@<version>`. */
  readonly reference: string;
  readonly name: string;
  readonly description: string;
  readonly stepCount: number;
  /** A step reads the bound source version: the Workflow can update a corpus from a hosted source. */
  readonly readsSourceVersion: boolean;
  /** The project runs it: one of its own, or a library Workflow its bindings name; pickers offer these first. */
  readonly runsInProject: boolean;
  /** It leaves a required step for the Workflow that nests it to bind, so it never starts a Run itself (ADR-0043 D1). */
  readonly abstract: boolean;
  /** What it takes to start (ADR-0032 D3, F28): declared, or derived from its steps. */
  readonly inputs: readonly WorkflowInputSummary[];
  /** The job a shipped Workflow does, so the app finds it without its name or ID (ADR-0031 D3); `null` for any other. */
  readonly purpose: WorkflowPurpose | null;
  /** The library project it comes from; `null` for the project's own. */
  readonly library: string | null;
  /** It comes from the DMW library the server installs, the DMW basis every project inherits (F63, F97). */
  readonly fromBasis: boolean;
  /** The listed Workflows with a step that runs this one: it is a part of them (F97). */
  readonly partOf: readonly ParentWorkflow[];
  /** The IRI of its most specific DMW class: `dmwp:Workflow`, or its purpose's class; `termLabel` names it (processes.md P-19). */
  readonly kindClass: string;
  /** The more general Workflow it is a kind of (`kind_of`, P-19); `null` for none. */
  readonly kindOf: GeneralWorkflow | null;
  /** What it is for: the IRI of the work area it serves (a `dmwp:WorkArea`, processes.md P-20), by which Workflow lists group it; `null` for none. */
  readonly theme: string | null;
  /** What a list's search finds it by, as the server says: its name and description in every language it has and its inputs' labels (P-21). */
  readonly searchText: string;
}

/** One input a Workflow takes to start. */
export interface WorkflowInputSummary {
  readonly name: string;
  /** `<flat step key>.<step input>`: the step input it fills, the key a start's values are stored under. */
  readonly destination: string;
  /** In the reader's language, by the server's one rule for input labels. */
  readonly label: string;
  /** The data types it accepts: a value of any of them. */
  readonly accepts: readonly DataTypeRef[];
  readonly required: boolean;
}

/** A more general Workflow at its pinned version; `name` is `null` where the pin does not resolve. */
export interface GeneralWorkflow {
  readonly reference: string;
  readonly name: string | null;
}

/** A Workflow that runs another as one of its steps. */
export interface ParentWorkflow {
  readonly reference: string;
  readonly name: string;
}

export type WorkflowPurpose = ApiSchema<"WorkflowPurpose">;

export const WORKFLOW_PURPOSES = enumMembers<WorkflowPurpose>({ edit_item: true, keep_memory: true, review_memories: true, draft_questions: true, accept_results: true, present_documents: true });

/** The work area a Workflow serves, as its file names it (processes.md P-20). */
export type WorkArea = ApiSchema<"WorkArea">;

export const WORK_AREAS = enumMembers<WorkArea>({ corpus: true, ontology: true, knowledge_data: true, memories: true, process: true });

/** The project's model versions, newest first. */
export function listContentVersions(projectId: string, signal?: AbortSignal, commit?: string): Promise<readonly ContentVersion[]> {
  return requestJson({
    method: "GET",
    path: withQuery(`/projects/${encodeURIComponent(projectId)}/content/versions`, { version: commit ?? null }),
    signal,
    decodeResponse: (body) => readArray(body, "model versions", (item) => readCollectionVersionSummary(item, "model version")),
  });
}

/**
 * Opens the project's content if it has none yet, as its first, empty model version, and
 * returns the newest model version: what a free Thread in a new project binds (T-3).
 */
export function openProjectContent(projectId: string): Promise<string> {
  return requestJson({
    method: "POST",
    path: `/projects/${encodeURIComponent(projectId)}/content`,
    decodeResponse: (body) => readString(readObject(body, "project content"), "latest_version"),
  });
}

/** The Workflows of one model version (its commit), or of the latest with `null`. */
export function listWorkflows(projectId: string, commit: string | null, signal?: AbortSignal): Promise<WorkflowListing> {
  return requestJson({
    method: "GET",
    path: withQuery(`/projects/${encodeURIComponent(projectId)}/content/workflows`, { version: commit }),
    signal,
    decodeResponse: decodeWorkflowListing,
  });
}

/** The body of `GET /projects/{project_id}/content/workflows`. */
export function decodeWorkflowListing(body: unknown): WorkflowListing {
  const listing = readObject(body, "Workflow listing");
  return {
    version: readString(listing, "version"),
    workflows: readArray(listing["workflows"], "Workflows", (item) => {
      const workflow = readObject(item, "Workflow");
      return {
        reference: readString(workflow, "reference"),
        name: readString(workflow, "name"),
        description: readString(workflow, "description"),
        stepCount: readInteger(workflow, "step_count"),
        readsSourceVersion: readBoolean(workflow, "reads_source_version"),
        runsInProject: readBoolean(workflow, "runs_in_project"),
        abstract: readBoolean(workflow, "abstract"),
        inputs: readArray(workflow["inputs"], "Workflow inputs", (value) => {
          const input = readObject(value, "Workflow input");
          return {
            name: readString(input, "name"),
            destination: readString(input, "destination"),
            label: readString(input, "label"),
            accepts: readArray(input["accepts"], "accepted types", readTypeRef),
            required: readBoolean(input, "required"),
          };
        }),
        purpose: workflow["purpose"] == null ? null : readOneOf(workflow, "purpose", WORKFLOW_PURPOSES),
        library: workflow["library"] === undefined ? null : readNullableString(workflow, "library"),
        fromBasis: workflow["from_basis"] === true,
        partOf: workflow["part_of"] === undefined ? [] : readArray(workflow["part_of"], "parent Workflows", (item) => {
          const parent = readObject(item, "parent Workflow");
          return { reference: readString(parent, "reference"), name: readString(parent, "name") };
        }),
        kindClass: readString(workflow, "kind_class"),
        kindOf: workflow["kind_of"] == null ? null : readGeneralWorkflow(readObject(workflow["kind_of"], "general Workflow")),
        theme: workflow["theme"] == null ? null : readString(workflow, "theme"),
        searchText: readString(workflow, "search_text"),
      };
    }),
  };
}

/**
 * The Workflows module B would start at a listing's model version from nothing at hand that
 * give the wanted data type or a subclass of it (`prepare`'s `wanted_output`, ADR-0032 D3, D7):
 * their `VersionRef`s. A view offers these; B fits. Writes nothing.
 */
export function listWorkflowsGiving(projectId: string, version: string, wanted: string, signal?: AbortSignal): Promise<ReadonlySet<string>> {
  return requestJson({
    method: "POST",
    path: `/projects/${encodeURIComponent(projectId)}/content/prepare`,
    body: jsonBody({ version: parseCollectionVersionRef(version)?.commit ?? null, wanted_output: { type: wanted } }),
    signal,
    decodeResponse: (body) => new Set(readArray(readObject(body, "preparation")["workflows"], "prepared Workflows", (item) => readString(readObject(item, "prepared Workflow"), "reference"))),
  });
}

function readGeneralWorkflow(general: JsonObject): GeneralWorkflow {
  return { reference: readString(general, "reference"), name: readNullableString(general, "name") };
}

/** Every step a project can use at one model version: its own and those of the libraries it pins (F36). */
export interface StepListing {
  /** The model version listed. */
  readonly version: string;
  readonly steps: readonly ListedStep[];
}

export interface ListedStep {
  /** `VersionRef` `<item id>@<version>`. */
  readonly reference: string;
  readonly name: string;
  readonly description: string;
  readonly stepKind: "agent" | "human" | "tool";
  /** The pinned library project the step comes from; `null` for the project's own. */
  readonly library: string | null;
  readonly inputs: readonly ListedStepInput[];
  readonly outputs: readonly ListedStepOutput[];
  /** Module B's searchable words in every language the step has (P-21). */
  readonly searchText: string;
}

/**
 * A data type a step takes or gives (ADR-0032 D1): a class of the DMW process ontology or of
 * the project's pinned ontology modules, its cardinality, the segment type a segment takes,
 * and the item classes a change set may hold.
 */
export interface DataTypeRef {
  readonly type: string;
  readonly many: boolean;
  readonly segmentType: string | null;
  readonly holds: readonly string[];
}

/** One declared input slot (W-4) and what fills it by default. */
export interface ListedStepInput {
  readonly name: string;
  /** The data types it accepts: a value of any of them. */
  readonly accepts: readonly DataTypeRef[];
  readonly required: boolean;
  /** Its default value as JSON; `null` without one. */
  readonly defaultValue: unknown;
  readonly label: string;
  readonly description: string;
}

export interface ListedStepOutput {
  readonly name: string;
  /** The data types it may give: one of them. */
  readonly produces: readonly DataTypeRef[];
}

export function readTypeRef(value: unknown): DataTypeRef {
  const reference = readObject(value, "data type");
  return {
    type: readString(reference, "type"),
    many: reference["many"] === true,
    segmentType: readOptionalString(reference, "segment_type") ?? null,
    holds: reference["holds"] === undefined ? [] : readArray(reference["holds"], "held classes", (held) => readString({ held }, "held")),
  };
}

const STEP_KINDS = ["agent", "human", "tool"] as const;

/**
 * The steps of one model version (its commit), or of the latest with `null`, optionally
 * only those whose name or description matches `query`.
 */
export function listSteps(projectId: string, commit: string | null, query: string | null, signal?: AbortSignal): Promise<StepListing> {
  return requestJson({
    method: "GET",
    path: withQuery(`/projects/${encodeURIComponent(projectId)}/content/steps`, { version: commit, query }),
    signal,
    decodeResponse: decodeStepListing,
  });
}

/** A talk step a review may offer (F105), at its exact version. */
export interface ListedTalkStep {
  /** `VersionRef` `<item id>@<version>`: the pin a review's `talk` names. */
  readonly reference: string;
  readonly name: string;
}

/** The talk steps of one model version, or of the latest with `null`: the project's own and its libraries'. A Workflow never places one. */
export function listTalkSteps(projectId: string, commit: string | null, signal?: AbortSignal): Promise<readonly ListedTalkStep[]> {
  return requestJson({
    method: "GET",
    path: withQuery(`/projects/${encodeURIComponent(projectId)}/content/steps`, { version: commit, kind: "talk" }),
    signal,
    decodeResponse: decodeTalkStepListing,
  });
}

function decodeTalkStepListing(body: unknown): readonly ListedTalkStep[] {
  return readArray(readObject(body, "step listing")["steps"], "steps", (item) => {
    const step = readObject(item, "step");
    readOneOf(step, "step_kind", ["talk"] as const);
    return { reference: readString(step, "reference"), name: readString(step, "name") };
  });
}

export function decodeStepListing(body: unknown): StepListing {
  const listing = readObject(body, "step listing");
  return {
    version: readString(listing, "version"),
    steps: readArray(listing["steps"], "steps", (item) => {
      const step = readObject(item, "step");
      return {
        reference: readString(step, "reference"),
        name: readString(step, "name"),
        description: readString(step, "description"),
        stepKind: readOneOf(step, "step_kind", STEP_KINDS),
        searchText: readString(step, "search_text"),
        library: readNullableString(step, "library"),
        inputs: readArray(step["inputs"], "step inputs", (value) => {
          const input = readObject(value, "step input");
          return {
            name: readString(input, "name"),
            accepts: readArray(input["accepts"], "accepted types", readTypeRef),
            required: readBoolean(input, "required"),
            defaultValue: input["default"] ?? null,
            label: readString(input, "label"),
            description: readString(input, "description"),
          };
        }),
        outputs: readArray(step["outputs"], "step outputs", (value) => {
          const output = readObject(value, "step output");
          return { name: readString(output, "name"), produces: readArray(output["produces"], "produced types", readTypeRef) };
        }),
      };
    }),
  };
}
